import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "@/components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { useMemo, useState } from "react";
import { deleteBankAccountData, getShowBankAccount } from "@/Api/bankAccounts";
import { UpdateBtn } from "@/App/BankAccounts/Update";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { TableDataProps } from "@/pages/BankAccounts";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

type SortOrder = "asc" | "desc";

type TypeTableProps<T> = {
  dataApi: ApiDataProps<T>;
  pageActions: TypePageActions;
  updateFront: (data: any) => void;
};

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const [sortField, setSortField] = useState<keyof TableDataProps | "">("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const { hasPermission } = usePermissions();

  const handleSort = (field: keyof TableDataProps) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!dataApi?.data || !sortField) return dataApi?.data || [];

    return [...dataApi.data].sort((a, b) => {
      const valueA = a[sortField] || "";
      const valueB = b[sortField] || "";

      if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
      if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [dataApi?.data, sortField, sortOrder]);

  const renderSortIndicator = (field: keyof TableDataProps) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Apelido</TableHead>
            <TableHead
              className="cursor-pointer"
              onClick={() => handleSort("name")}
            >
              Banco{renderSortIndicator("name")}
            </TableHead>
            <TableHead>Agência</TableHead>
            <TableHead>Conta</TableHead>
            <TableHead>Unidade</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData && sortedData.length > 0 ? (
            sortedData.map((item, index) => (
              <TableRow key={item?.id}>
                <TableCell>{item?.surname}</TableCell>
                <TableCell>{item?.name}</TableCell>
                <TableCell>{item?.agency}</TableCell>
                <TableCell>{item?.account_number}</TableCell>
                <TableCell>{item?.unity?.name}</TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-bank-account")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-bank-account")}>
                      <DeleteBtn
                        params={{
                          func: deleteBankAccountData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-bank-account-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Conta Bancária" }}
                        params={{
                          func: getShowBankAccount,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <div className="w-full h-auto">
                            <div className="flex flex-col gap-3">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <p className="text-sm text-gray-500">Nome</p>
                                  <p>{responseApi?.name}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <p className="text-sm text-gray-500">
                                    Agência
                                  </p>
                                  <p>{responseApi?.agency}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Conta</p>
                                  <p>{responseApi?.account_number}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                Nenhuma conta bancária encontrada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {dataApi?.last_page > 1 && (
        <div className="flex justify-between mb-3">
          {dataApi?.data && dataApi?.data.length > 0 && (
            <span className="font-medium">
              Exibindo {dataApi?.current_page} de um total de{" "}
              {dataApi?.last_page}
            </span>
          )}
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  );
};
